/**
 * Click / interaction tracking for the redirect gate.
 *
 * Captures every click (and key redirect lifecycle events) from the moment the
 * redirect turns ON until it is closed, then persists a single session document
 * per redirect to Firestore at `clickTracking/{sessionId}`.
 *
 * Cross-origin constraint: clicks that land *inside* the offer iframe belong to a
 * different origin and cannot be read by this (parent) page — browsers block that
 * by design. We therefore record every click the parent document can observe
 * (the gate page, the embed wrapper/hold-zone, fullscreen toggles, etc.) and flag
 * whether the click was over the embed region (`inEmbed`).
 */
import db from '../firebase/firebaseConfig';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { REDIRECT_EMBED_ROOT_SEL } from './redirectShortlink';

const COLLECTION = 'clickTracking';
const VISITOR_KEY = 'hcva_visitor_id';

/** Keep the events array well under Firestore's 1MB doc limit. */
const MAX_EVENTS = 800;
/** Debounce window for batching buffered events into a single write. */
const FLUSH_DEBOUNCE_MS = 1500;

const hasWindow = typeof window !== 'undefined';
const hasDoc = typeof document !== 'undefined';

function randomId(len = 20) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < len; i += 1) {
    s += chars[Math.floor(Math.random() * chars.length)];
  }
  return s;
}

/** Stable anonymous visitor id (random, persisted in localStorage). */
export function getVisitorId() {
  if (!hasWindow) return 'ssr';
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = `v_${Date.now().toString(36)}_${randomId(12)}`;
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (_) {
    return `v_${randomId(12)}`;
  }
}

function detectDeviceType(ua) {
  const s = (ua || '').toLowerCase();
  if (/ipad|tablet|(android(?!.*mobile))/.test(s)) return 'tablet';
  if (/mobi|iphone|ipod|android.*mobile|windows phone/.test(s)) return 'mobile';
  return 'desktop';
}

function collectDeviceInfo() {
  if (!hasWindow) return { deviceType: 'ssr' };
  const ua = navigator.userAgent || '';
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    timezone = '';
  }
  return {
    userAgent: ua,
    platform: navigator.platform || '',
    language: navigator.language || '',
    deviceType: detectDeviceType(ua),
    screenW: window.screen?.width || 0,
    screenH: window.screen?.height || 0,
    viewportW: window.innerWidth || 0,
    viewportH: window.innerHeight || 0,
    devicePixelRatio: window.devicePixelRatio || 1,
    timezone,
  };
}

function isFullscreen() {
  if (!hasDoc) return false;
  return Boolean(
    document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
  );
}

/** Public fullscreen check (used by callers to label enter/exit). */
export function isFullscreenActive() {
  return isFullscreen();
}

/** Compact, safe description of the clicked element (no PII beyond short text). */
function describeTarget(target) {
  if (!target || typeof target !== 'object') return null;
  const tag = (target.tagName || '').toLowerCase();
  if (!tag) return null;
  let text = '';
  try {
    text = (target.innerText || target.textContent || '').trim().slice(0, 80);
  } catch (_) {
    text = '';
  }
  let cls = '';
  try {
    cls =
      typeof target.className === 'string'
        ? target.className.slice(0, 120)
        : '';
  } catch (_) {
    cls = '';
  }
  return {
    tag,
    id: target.id || '',
    cls,
    text,
    href: target.getAttribute ? target.getAttribute('href') || '' : '',
  };
}

/** Active session state (module singleton — one redirect gate at a time). */
let session = null;
let flushTimer = null;
let lifecycleBound = false;

function clearFlushTimer() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

function buildPayload(final) {
  if (!session) return null;
  const events = session.events.slice(-MAX_EVENTS);
  return {
    site: session.site,
    sessionId: session.id,
    visitorId: session.visitorId,
    redirectUrl: session.redirectUrl,
    entryPath: session.entryPath,
    referrer: session.referrer,
    device: session.device,
    startedAt: session.startedAtTs,
    startedAtMs: session.startedAtMs,
    lastActivityAt: serverTimestamp(),
    lastActivityMs: Date.now(),
    totalClicks: session.clickCount,
    totalEvents: session.events.length,
    eventsTruncated: session.events.length > MAX_EVENTS,
    events,
    closeSummary: computeCloseSummary(events, final ? session.endedReason : null),
    closed: Boolean(final),
    ...(final
      ? {
          endedAt: serverTimestamp(),
          endedAtMs: Date.now(),
          endedReason: session.endedReason || 'unknown',
          durationMs: Date.now() - session.startedAtMs,
        }
      : {}),
  };
}

async function writeSession(final) {
  if (!session) return;
  const payload = buildPayload(final);
  if (!payload) return;
  session.dirty = false;
  try {
    await setDoc(doc(db, COLLECTION, session.id), payload, { merge: true });
  } catch (err) {
    session.dirty = true;
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[clickTracking] flush failed:', err);
    }
  }
}

function scheduleFlush() {
  if (!session) return;
  session.dirty = true;
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    if (session && session.dirty) writeSession(false);
  }, FLUSH_DEBOUNCE_MS);
}

function onPageHide() {
  if (session) {
    pushEvent('browser_page_hide', {
      reason: 'pagehide',
      visibilityState: hasDoc ? document.visibilityState || '' : '',
      inFullscreen: isFullscreen(),
    });
    if (session.dirty) writeSession(false);
  }
}

function onVisibilityChange() {
  if (hasDoc && document.visibilityState === 'hidden') onPageHide();
}

function bindLifecycle() {
  if (lifecycleBound || !hasWindow) return;
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('beforeunload', onPageHide);
  if (hasDoc) document.addEventListener('visibilitychange', onVisibilityChange);
  lifecycleBound = true;
}

function unbindLifecycle() {
  if (!lifecycleBound || !hasWindow) return;
  window.removeEventListener('pagehide', onPageHide);
  window.removeEventListener('beforeunload', onPageHide);
  if (hasDoc) document.removeEventListener('visibilitychange', onVisibilityChange);
  lifecycleBound = false;
}

function pushEvent(type, data) {
  if (!session) return;
  session.seq += 1;
  session.events.push({
    seq: session.seq,
    t: Date.now(),
    type,
    ...data,
  });
  if (session.events.length > MAX_EVENTS + 50) {
    // Trim oldest, but always keep the very first (session_start) for context.
    const first = session.events[0];
    session.events = [first, ...session.events.slice(-(MAX_EVENTS - 1))];
  }
  scheduleFlush();
}

const CLOSE_WAY_LABELS = {
  esc_button: 'Esc key',
  f11_key: 'F11',
  back_button: 'Back button',
  tab_close: 'Tab close / reload',
  'browser/system': 'Browser / system',
};

/** Recent Esc / F11 / back attempts — used when the browser exits FS before our ref updates. */
const FS_EXIT_ATTEMPT_WINDOW_MS = 5000;
let lastFsExitAttempt = { way: '', at: 0 };
let lastEscKeyAt = 0;

export function noteFullscreenExitAttempt(way) {
  if (!way) return;
  lastFsExitAttempt = { way, at: Date.now() };
}

export function noteEscKeyActivity() {
  lastEscKeyAt = Date.now();
}

export function resolveFullscreenCloseWay(fallback = 'browser/system') {
  const { way, at } = lastFsExitAttempt;
  if (way && Date.now() - at < FS_EXIT_ATTEMPT_WINDOW_MS) return way;
  if (Date.now() - lastEscKeyAt < FS_EXIT_ATTEMPT_WINDOW_MS) return 'esc_button';
  return fallback;
}

export function clearFullscreenExitAttempt() {
  lastFsExitAttempt = { way: '', at: 0 };
}

/** Roll up how the user tried to leave (for admin closeSummary on each session doc). */
export function computeCloseSummary(events, endedReason) {
  const summary = {
    tabCloseAttempts: 0,
    pageHideCount: 0,
    fullscreenExitCount: 0,
    lastFullscreenExitWay: '',
    lastFullscreenExitAtMs: 0,
    f11Attempts: 0,
    escAttempts: 0,
    backAttempts: 0,
    escPressCount: 0,
    escReleaseCount: 0,
    autoScanClicks: 0,
    sessionEndReason: endedReason || '',
    primaryExitLabel: '',
    details: [],
  };

  (events || []).forEach((ev) => {
    switch (ev.type) {
      case 'tab_close_attempt':
        summary.tabCloseAttempts += 1;
        break;
      case 'browser_page_hide':
        summary.pageHideCount += 1;
        break;
      case 'fullscreen_exit':
        summary.fullscreenExitCount += 1;
        summary.lastFullscreenExitWay = ev.closeWay || summary.lastFullscreenExitWay;
        summary.lastFullscreenExitAtMs = ev.t || summary.lastFullscreenExitAtMs;
        break;
      case 'fullscreen_exit_attempt':
        if (ev.way === 'f11_key') summary.f11Attempts += 1;
        if (ev.way === 'esc_button') summary.escAttempts += 1;
        if (ev.way === 'back_button') summary.backAttempts += 1;
        break;
      case 'esc_press':
        summary.escPressCount += 1;
        break;
      case 'esc_release':
        summary.escReleaseCount += 1;
        break;
      case 'auto_scan_click':
        summary.autoScanClicks += 1;
        break;
      default:
        break;
    }
  });

  const detailParts = [];
  if (summary.tabCloseAttempts) {
    detailParts.push(`Tab close/reload (${summary.tabCloseAttempts}×)`);
  }
  if (summary.pageHideCount) {
    detailParts.push(`Browser hidden (${summary.pageHideCount}×)`);
  }
  if (summary.fullscreenExitCount) {
    const wayLabel =
      CLOSE_WAY_LABELS[summary.lastFullscreenExitWay] ||
      summary.lastFullscreenExitWay ||
      'Unknown';
    detailParts.push(`Fullscreen exit: ${wayLabel} (${summary.fullscreenExitCount}×)`);
  }
  if (summary.f11Attempts) detailParts.push(`F11 blocked (${summary.f11Attempts}×)`);
  if (summary.escAttempts) detailParts.push(`Esc blocked (${summary.escAttempts}×)`);
  if (summary.backAttempts) detailParts.push(`Back while FS (${summary.backAttempts}×)`);
  if (summary.escPressCount) detailParts.push(`Esc presses (${summary.escPressCount}×)`);
  if (summary.sessionEndReason && summary.sessionEndReason !== 'unknown') {
    detailParts.push(`Session end: ${summary.sessionEndReason}`);
  }

  summary.details = detailParts;
  summary.primaryExitLabel =
    detailParts[0] ||
    (summary.fullscreenExitCount
      ? `Fullscreen exit (${CLOSE_WAY_LABELS[summary.lastFullscreenExitWay] || summary.lastFullscreenExitWay || 'unknown'})`
      : 'No exit recorded');

  return summary;
}

/**
 * Start a tracking session when the redirect turns ON.
 * Safe to call repeatedly for the same id (no-op if already active for that id).
 */
export function startClickSession({ site, redirectUrl, entryPath } = {}) {
  if (!hasWindow) return null;
  if (session && session.site === site && session.redirectUrl === redirectUrl) {
    return session.id; // already tracking this redirect
  }
  // A different redirect started before the previous closed — close the old one.
  if (session) endClickSession('superseded');

  const now = Date.now();
  session = {
    id: `s_${now.toString(36)}_${randomId(10)}`,
    site: site || '',
    visitorId: getVisitorId(),
    redirectUrl: redirectUrl || '',
    entryPath: entryPath || (hasWindow ? window.location.pathname : ''),
    referrer: hasDoc ? document.referrer || '' : '',
    device: collectDeviceInfo(),
    startedAtTs: serverTimestamp(),
    startedAtMs: now,
    events: [],
    seq: 0,
    clickCount: 0,
    dirty: false,
    endedReason: null,
  };

  bindLifecycle();
  pushEvent('session_start', {
    path: entryPath || (hasWindow ? window.location.pathname : ''),
    fullscreen: isFullscreen(),
  });
  // Write the opening document immediately so partial sessions are never lost.
  writeSession(false);
  return session.id;
}

/** Record a raw click anywhere on the page while the gate is active. */
export function recordClick(e) {
  if (!session || !e) return;
  const target = e.target;
  const inEmbed = Boolean(
    target && target.closest && target.closest(REDIRECT_EMBED_ROOT_SEL)
  );
  session.clickCount += 1;
  pushEvent('click', {
    x: Math.round(e.clientX || 0),
    y: Math.round(e.clientY || 0),
    pageX: Math.round(e.pageX || 0),
    pageY: Math.round(e.pageY || 0),
    button: typeof e.button === 'number' ? e.button : 0,
    vw: hasWindow ? window.innerWidth : 0,
    vh: hasWindow ? window.innerHeight : 0,
    path: hasWindow ? window.location.pathname : '',
    fullscreen: isFullscreen(),
    inEmbed,
    target: describeTarget(target),
  });
}

/** Record a non-click lifecycle event (fullscreen, navigation, popup, back-trap...). */
export function recordLifecycle(type, data = {}) {
  if (!session) return;
  pushEvent(type, {
    path: hasWindow ? window.location.pathname : '',
    fullscreen: isFullscreen(),
    ...data,
  });
}

/** Close the active session (redirect turned off / left the flow). Flushes immediately. */
export function endClickSession(reason = 'closed') {
  if (!session) return;
  session.endedReason = reason;
  pushEvent('session_end', {
    reason,
    fullscreen: isFullscreen(),
    durationMs: Date.now() - session.startedAtMs,
  });
  clearFlushTimer();
  const finalWrite = writeSession(true);
  unbindLifecycle();
  session = null;
  return finalWrite;
}

export function isTracking() {
  return Boolean(session);
}
